/**
 * Kiosk Connector Card Component
 * Displays individual connector information with status
 * Based on Figma connector card designs (node-id=3905-15252)
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { KioskUICustomization } from '../../types/kiosk-types';
import { ConnectorType } from '../../constants/connector-types';

interface Connector {
  id: string;
  type: ConnectorType;
  power: number;
  status: 'available' | 'connected' | 'occupied' | 'unavailable';
}

interface KioskConnectorCardProps {
  connector: Connector;
  config: KioskUICustomization;
  size: 'small' | 'large';
}

/**
 * Get status color from theme based on status
 */
function getStatusColor(
  status: Connector['status'],
  config: KioskUICustomization
): string {
  switch (status) {
    case 'available':
      return config.theme.colors.statusSuccess;
    case 'connected':
      return config.theme.colors.statusInfo;
    case 'occupied':
      return config.theme.colors.statusWarning;
    case 'unavailable':
      return config.theme.colors.statusError;
    default:
      return config.theme.colors.statusError;
  }
}

/**
 * Get status label
 */
function getStatusLabel(status: Connector['status']): string {
  switch (status) {
    case 'available':
      return 'Available';
    case 'connected':
      return 'Connected';
    case 'occupied':
      return 'Occupied';
    case 'unavailable':
      return 'Unavailable';
    default:
      return 'Unknown';
  }
}

/**
 * Get connector type display name
 */
function getConnectorTypeName(type: ConnectorType): string {
  switch (type) {
    case 'ccs':
      return 'CCS';
    case 'chademo':
      return 'CHAdeMO';
    case 'type2':
      return 'Type 2';
    case 'gbt':
      return 'GB/T';
    default:
      return type.toUpperCase();
  }
}

/**
 * Kiosk Connector Card Component
 * Exact Figma design: 450x114px (4"), 660x200px (5")
 */
export function KioskConnectorCard({
  connector,
  config,
  size,
}: KioskConnectorCardProps) {
  const isSmall = size === 'small';
  const statusColor = getStatusColor(connector.status, config);
  const statusLabel = getStatusLabel(connector.status);
  const connectorName = getConnectorTypeName(connector.type);

  // Dimensions based on Figma design
  // 4": 450x114px | 5": 385x117px (scaled from 660x200 by 0.583x)
  const cardWidth = isSmall ? '450px' : '385px';
  const cardHeight = isSmall ? '114px' : '117px';

  // Get connector icon from config
  const connectorIcon = config.icons[connector.type];

  return (
    <div
      className="relative overflow-hidden transition-all hover:scale-[1.02]"
      style={{
        width: cardWidth,
        height: cardHeight,
        backgroundColor: config.theme.colors.secondaryBackground,
        borderRadius: '7px',
        border: `0.47px solid ${config.theme.colors.secondaryBackground}`,
        cursor: 'pointer',
      }}
    >
      {/* Gradient header bar with connector ID - top-left corner */}
      <div
        className="absolute left-0 top-0 flex items-center px-3 py-1"
        style={{
          width: isSmall ? '227px' : '192px',
          height: isSmall ? '32px' : '29px',
          background: 'linear-gradient(90deg, rgba(87, 87, 87, 1) 30%, rgba(38, 38, 38, 1) 97%)',
          borderRadius: '3.29px 3.29px 0px 0px',
        }}
      >
        <span
          className="font-semibold"
          style={{
            color: '#FFFFFF',
            fontSize: isSmall ? '16px' : '14px',
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          {connector.id}
        </span>
      </div>

      {/* Main content area */}
      <div className="flex h-full items-center px-2 pt-1">
        {/* Connector icon - left side */}
        <div
          className="flex shrink-0 items-center justify-center"
          style={{
            width: isSmall ? '66px' : '58px',
            height: isSmall ? '64px' : '56px',
            marginTop: isSmall ? '8px' : '7px',
          }}
        >
          {connectorIcon?.s3Url || connectorIcon?.fallbackUrl ? (
            <img
              src={connectorIcon.s3Url || connectorIcon.fallbackUrl}
              alt={connectorName}
              className="h-full w-full object-contain"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center rounded-lg"
              style={{
                backgroundColor: config.theme.colors.primaryBackground,
                color: config.theme.colors.secondaryText,
              }}
            >
              <svg
                width={isSmall ? '40' : '60'}
                height={isSmall ? '40' : '60'}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.5 }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            </div>
          )}
        </div>

        {/* Status and type info - middle */}
        <div className="ml-3 flex flex-1 flex-col justify-center" style={{ marginTop: isSmall ? '20px' : '17px' }}>
          {/* Status text with color */}
          <div
            className="font-semibold"
            style={{
              color: statusColor,
              fontSize: isSmall ? '14px' : '12px',
              fontFamily: 'Outfit, sans-serif',
              lineHeight: '1.2',
            }}
          >
            {statusLabel}
          </div>
          {/* Connector type and power */}
          <div
            className="mt-1"
            style={{
              color: config.theme.colors.primaryText,
              fontSize: isSmall ? '18px' : '15px',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 400,
              lineHeight: '1.2',
            }}
          >
            {connectorName} DC {connector.power}kW
          </div>
        </div>

        {/* Arrow icon - right side */}
        <div className="flex shrink-0 items-center" style={{ marginTop: isSmall ? '20px' : '17px' }}>
          <ChevronRight
            style={{
              color: '#707070',
              width: isSmall ? '19px' : '16px',
              height: isSmall ? '19px' : '16px',
              strokeWidth: '3px',
            }}
          />
        </div>
      </div>
    </div>
  );
}
